import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useInView } from "./Reveal";

const EASE = "ease-[cubic-bezier(.76,0,.24,1)]";

// Text that rolls up on hover. Parent needs the class `group/roll`.
export function Roll({ children, className = "" }) {
  const t = `transition-transform duration-500 ${EASE} group-hover/roll:-translate-y-full group-focus-visible/roll:-translate-y-full`;
  return (
    <span className={`relative inline-flex overflow-hidden ${className}`} style={{ lineHeight: 1.3 }}>
      <span className={`block ${t}`}>{children}</span>
      <span aria-hidden="true" className={`absolute inset-x-0 top-full block ${t}`}>{children}</span>
    </span>
  );
}

const VARIANTS = {
  dark: "bg-black text-white hover:bg-[#262626]",
  light: "bg-[#f7f7f7] text-black hover:bg-black hover:text-white",
  white: "bg-white text-black hover:bg-black hover:text-white",
  inverse: "bg-white text-black hover:bg-[#e6e6e6]",
};

export function PillButton({ to, href, onClick, type, variant = "dark", size = "md", icon = true, children, className = "", ...rest }) {
  const cls = `group/roll nm-pill-btn inline-flex shrink-0 items-center gap-2 rounded-full font-medium tracking-[-0.01em] ${
    size === "lg" ? "h-11 px-6 text-[13px]" : "h-9 px-5 text-[12px]"
  } ${VARIANTS[variant]} ${className}`;
  const inner = (
    <>
      <Roll>{children}</Roll>
      {icon && (
        <ArrowUpRight size={12} className={`transition-transform duration-500 ${EASE} group-hover/roll:translate-x-0.5 group-hover/roll:-translate-y-0.5`} />
      )}
    </>
  );
  if (to) return <Link to={to} className={cls} {...rest}>{inner}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} className={cls} {...rest}>{inner}</button>;
}

export function LiveClock({ tz, label }) {
  const [t, setT] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const s = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(t);
  return (
    <span className="inline-flex items-center gap-2 tabular-nums">
      <span className="opacity-40">{label}</span>
      {s}
    </span>
  );
}

export function Counter({ from = 0, to, suffix = "", duration = 1800 }) {
  const [ref, seen] = useInView(0.4);
  const [val, setVal] = useState(from);
  useEffect(() => {
    if (!seen) return undefined;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      setVal(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 4))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, from, to, duration]);
  return <span ref={ref} className="tabular-nums">{val}{suffix}</span>;
}

// Deterministic little geometric logo marks for the placeholder clients
const MARKS = [
  <><circle cx="10" cy="10" r="7" /><circle cx="10" cy="10" r="2.4" fill="currentColor" /></>,
  <rect x="4" y="4" width="12" height="12" transform="rotate(45 10 10)" />,
  <path d="M3 16 10 4l7 12z" />,
  <path d="M3 5h14M3 10h14M3 15h9" />,
  <><circle cx="10" cy="10" r="7" /><path d="M10 3a7 7 0 0 1 0 14z" fill="currentColor" /></>,
  <path d="M4 16V4l12 12V4" />,
  <><path d="M3 10h14" /><path d="M10 3v14" /><circle cx="10" cy="10" r="6" /></>,
  <path d="M3 14c3-8 5 6 7-2s4 2 7-6" />,
];
const hash = (s) => [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
const WORD = [
  "text-[11px] uppercase tracking-[0.12em] font-medium",
  "text-[16px] font-medium tracking-[-0.025em]",
  "text-[14px] lowercase tracking-[-0.02em]",
];

export function ClientMark({ name }) {
  const h = hash(name);
  return (
    <span className="flex flex-col items-center gap-2.5">
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
        {MARKS[h % MARKS.length]}
      </svg>
      <span className={WORD[(h >> 3) % WORD.length]}>{name}</span>
    </span>
  );
}

// Curtain + scale reveal for images
export function ImageReveal({ children, className = "", delay = 0 }) {
  const [ref, seen] = useInView(0.1);
  return (
    <div ref={ref}>
      {/* Observe the full layout box: the curtain's initial clip has no visible intersection. */}
      <div style={{ transitionDelay: `${delay}ms` }} className={`nm-clip ${seen ? "is-in" : ""} ${className}`}>
        {children}
      </div>
    </div>
  );
}

// Scroll-linked parallax; parent must be overflow-hidden
export function Parallax({ children, speed = 0.08, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf = 0;
    const upd = () => {
      raf = 0;
      const r = el.parentElement.getBoundingClientRect();
      const c = r.top + r.height / 2 - window.innerHeight / 2;
      el.style.transform = `translate3d(0,${(-c * speed).toFixed(1)}px,0) scale(1.2)`;
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(upd); };
    upd();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
      cancelAnimationFrame(raf);
    };
  }, [speed]);
  return <div ref={ref} className={`h-full w-full will-change-transform ${className}`}>{children}</div>;
}
